import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Menu, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { isTablet } from 'react-device-detect';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { MENUS, SUPER_ADMIN_EMAILS, SUPER_ADMIN_MENUS } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import { useI18n } from 'hooks/useI18n';
import './sidebar.scss';

const { Sider } = Layout;
const { SubMenu } = Menu;

export const LogoComponent = ({ style = {}, collapsed = false, textColor = 'var(--primary-color, #4d7cfe)', profilePic = '' }) => (
	<Link to="/" className="logo_class" style={{ ...style, color: textColor }}>
		{profilePic ? (
			<img
				src={SERVER_IP + profilePic}
				alt="Profile"
				style={{
					width: collapsed ? 40 : 55, // Adjust size based on collapsed state
					height: collapsed ? 40 : 55,
					borderRadius: '50%',
					objectFit: 'cover',
					marginRight: collapsed ? 0 : 10, // Add space if not collapsed
				}}
			/>
		) : collapsed ? (
			<span>
				F<span style={{ color: 'var(--primary-color, #4d7cfe)' }}>T</span>
			</span>
		) : (
			<span>
				FITTE<span style={{ color: 'var(--primary-color, #4d7cfe)', paddingLeft: 1 }}>.in</span>
			</span>
		)}
	</Link>
);

// Function to translate menu names
const getMenuTranslation = (menuName, t) => {
	const menuTranslations = {
		'Dashboard': t('navigation.dashboard'),
		'Customers': t('navigation.customers'),
		'Bulk Upload': t('navigation.bulk_upload'),
		'Visitors': t('navigation.visitors'),
		'Personal Training': t('navigation.personal_training'),
		'Products': t('navigation.products'),
		'Coaches': t('navigation.coaches'),
		'Payments': t('navigation.payments'),
		'Outstanding': t('navigation.outstanding'),
		'Product Expense': t('navigation.product_expense'),
		'Masters': t('navigation.masters'),
		'Diet Plan': t('navigation.diet_plan'),
		'Reports': t('navigation.reports'),
		'Settings': t('navigation.settings'),
		'Password Setup': t('navigation.password_setup'),
		// Add more menu translations as needed
	};
	return menuTranslations[menuName] || menuName;
};

function Sidebar() {
	const { t } = useI18n();
	const loginRedux = useSelector((state) => state.loginRedux);
	const [collapsed, setCollapsed] = useState(false);

	const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(loginRedux?.email);
	const enabledMenuKeys = loginRedux?.enabledMenus;

	const isMenuLocked = (menu) => {
		if (isSuperAdmin) return false;
		if (!menu.accessKey) return false;
		if (!Array.isArray(enabledMenuKeys)) return false;
		return !enabledMenuKeys.includes(menu.accessKey);
	};

	const renderLockedLabel = (label) => (
		<span className="locked-menu-label">
			<span className="locked-menu-text">{label}</span>
			<LockOutlined className="menu-lock-icon" />
		</span>
	);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const visibleMenus = useMemo(() => {
		if (SUPER_ADMIN_EMAILS.includes(loginRedux?.email)) {
			return SUPER_ADMIN_MENUS;
		}

		return MENUS;
	}, [loginRedux]);

	/**
	 * @description: This function determines active menu & open submenus
	 */
	const { selectedKey, parentKey } = useMemo(() => {
		let selectedKey = '';
		let parentKey = '';

		visibleMenus.forEach((menu) => {
			if (menu.route && pathname.startsWith(menu.route)) {
				selectedKey = menu.key;
			}
			if (menu.submenus) {
				menu.submenus.forEach((submenu) => {
					if (submenu.route && pathname.startsWith(submenu.route)) {
						selectedKey = submenu.key;
						parentKey = menu.subName || menu.key;
					}
				});
			}
		});

		return { selectedKey, parentKey };
	}, [pathname, visibleMenus]);

	// Open parent menu if a submenu is selected
	const [openKeys, setOpenKeys] = useState(parentKey ? [parentKey] : []);

	/**
	 * @description This function is used to handle submenu expansion
	 */
	const onOpenChange = (keys) => {
		const latestOpenKey = keys.find((key) => !openKeys.includes(key));
		setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
	};

	useEffect(() => {
		if (isTablet) {
			setCollapsed(true);
		}
	}, []);

	return (
		<Sider
			breakpoint="md"
			collapsedWidth={80}
			collapsible
			collapsed={collapsed}
			onCollapse={() => setCollapsed(!collapsed)}
			style={{ height: '100vh', overflow: 'auto' }} // Ensures full height and enables scrolling
		>
			<LogoComponent style={{ color: 'var(--primary-color, #4d7cfe)' }} collapsed={collapsed} profilePic={loginRedux?.profilePic} />
			<Menu
				theme="light"
				mode="inline"
				selectedKeys={[selectedKey]}
				openKeys={openKeys}
				onOpenChange={onOpenChange}
				style={{ height: 'calc(100% - 64px)', overflowY: 'auto', paddingBottom: 48 }} // Enables vertical scrolling
			>
				{visibleMenus.map((menu) => {
					const hasSubMenu = !!menu.submenus;
					const Icon = menu.icon || <MailOutlined />;
					const locked = isMenuLocked(menu);
					const label = menu.labelKey ? t(menu.labelKey) : getMenuTranslation(menu.name, t);

					// Locked: render as a non-interactive item (SubMenus also collapse to a flat item)
					if (locked) {
						const hint = menu.accessKey ? t(`navigation.menu_hints.${menu.accessKey}`, { defaultValue: '' }) : '';
						const tooltipContent = (
							<div>
								{hint && <div style={{ marginBottom: 5 }}>{hint}</div>}
								<div style={{ opacity: 0.72, fontSize: 11 }}>{t('navigation.locked_menu_tooltip')}</div>
							</div>
						);
						return (
							<Tooltip
								key={menu.subName || menu.key}
								title={tooltipContent}
								placement="right"
								mouseEnterDelay={0.3}
							>
								<Menu.Item
									key={menu.subName || menu.key}
									icon={Icon}
									className="sidebar-menu-locked"
								>
									{renderLockedLabel(label)}
								</Menu.Item>
							</Tooltip>
						);
					}

					if (hasSubMenu) {
						return (
							<SubMenu key={menu.subName || menu.key} icon={Icon} title={label} className="sub_menu_style">
								{menu.submenus.map((submenu) => (
									<Menu.Item key={submenu.key} onClick={() => navigate(submenu.route)}>
										{submenu.labelKey ? t(submenu.labelKey) : getMenuTranslation(submenu.name, t)}
									</Menu.Item>
								))}
							</SubMenu>
						);
					}

					return (
						<Menu.Item key={menu.key} icon={Icon} onClick={() => navigate(menu.route)}>
							{label}
						</Menu.Item>
					);
				})}
			</Menu>
		</Sider>
	);
}

export default Sidebar;
